/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import BlockIcon from "@mui/icons-material/Block";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { BlueButton, GrayButton, GreenButton, RedButton } from "customComponents/CustomButtons";
import { UpdateCandidateNotes } from "customComponents/Modals/candidateNotes";
import CloseFormModal from "customComponents/Modals/CloseFormModal";
import CreateCandidatesView from "customComponents/Modals/createCandidatesViewModal";
import AddExcelModalV2 from "customComponents/Modals/V2/AddExcelModal";
import { isComponentAllowed } from "lib/utilities/componentAccess";
import { BASE_URL } from "lib/utilities/globalConstants";
import { handleDownloadCandidateData } from "lib/utilities/handleDownloadCandidateData";
import { requiredVariableDescV2, requiredVariablesV2 } from "lib/utilities/templates";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import PropTypes from "prop-types";
import { UserAuth } from "provider/AuthProvider";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const FormViewsCandidatesTable = ({ version, formId, viewId, token, viewData }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pagination, setPagination] = useState({
    pageIndex: parseInt(searchParams.get("pageIndex")) || 0,
    pageSize: parseInt(searchParams.get("pageSize")) || 5,
  });
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState(
    searchParams.get("sorting") ? JSON.parse(searchParams.get("sorting")) : []
  );
  const [columnFilters, setColumnFilters] = useState(
    searchParams.get("columnFilters") ? JSON.parse(searchParams.get("columnFilters")) : []
  );

  const [rowSelection, setRowSelection] = useState({});

  const navigate = useNavigate();
  const { user } = UserAuth();

  useEffect(() => {
    const params = {
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      globalFilter: globalFilter,
      sorting: JSON.stringify(sorting),
      columnFilters: JSON.stringify(columnFilters),
    };
    setSearchParams(params, { replace: true });
  }, [pagination, globalFilter, sorting, columnFilters]);

  const {
    data = [],
    isLoading,
    isRefetching,
    isError,
  } = useQuery({
    queryKey: [
      "getViewCandidates",
      pagination,
      viewId,
      token,
      columnFilters,
      sorting,
      globalFilter,
    ],
    queryFn: fetchCandidates,
    placeholderData: keepPreviousData,
    enabled: !!token && !!viewId,
    refetchOnWindowFocus: false,
  });

  async function fetchCandidates() {
    try {
      const res = await fetch(
        `${BASE_URL}admin/${version}/getViewCandidates?page=${pagination.pageIndex + 1}&pageSize=${
          pagination.pageSize
        }&columnFilters=${encodeURIComponent(JSON.stringify(columnFilters ?? []))}&globalFilter=${
          globalFilter ?? ""
        }&sorting=${JSON.stringify(sorting ?? [])}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            viewId,
            adminId: user.uid,
          }),
        }
      );
      const data = await res.json();
      return data;
    } catch (err) {
      console.log(err);
    }
  }

  const columns = [
    {
      header: "Name",
      accessorKey: "name",
      size: 130,
    },
    {
      header: "Email",
      accessorKey: "email",
      size: 250,
    },
    version === "v1" && {
      header: "Assignment Status",
      enableSorting: false,
      accessorKey: "assignmentStatus",
      size: 150,
      Cell: ({ cell }) => {
        const status = cell?.row?.original?.submissionId;
        return (
          <span
            style={{
              color: `${status !== null ? "green" : "#25408E"}`,
              fontWeight: 400,
            }}
          >
            {status !== null || undefined ? "SUBMITTED" : "SENT"}
          </span>
        );
      },
      Filter: ({ column }) => {
        const assignmentStatus = column.getFilterValue();
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.25rem",
              marginTop: "0.2rem",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "0.25rem",
              }}
            >
              <GreenButton
                label="Submitted"
                isActive={assignmentStatus === 1}
                onClick={() => column.setFilterValue("1")}
              />
              <BlueButton
                label="Sent"
                isActive={assignmentStatus === 0}
                onClick={() => column.setFilterValue("0")}
              />
            </div>
            <GrayButton label="Clear Filter" onClick={() => column.setFilterValue(undefined)} />
          </div>
        );
      },
    },
    version === "v1" && {
      header: "Assignment Score",
      accessorKey: "assignmentScore",
      size: 150,
      Cell: ({ cell }) => {
        const score = cell?.row?.original?.assignmentScore;
        return (
          <span
            style={{
              fontWeight: 400,
            }}
          >
            {score === null ? "-" : score}
          </span>
        );
      },
    },
    {
      header: "Interview Status",
      enableSorting: false,
      accessorKey: "interviewTaken",
      size: 150,
      Cell: ({ cell }) => {
        const status = cell?.row?.original?.interviewTaken;
        return (
          <span
            style={{
              color: `${status === 0 ? "#25408E" : status !== null ? "green" : ""}`,
              fontWeight: 400,
            }}
          >
            {status === null ? "-" : status === 0 ? "SCHEDULED" : "DONE"}
          </span>
        );
      },
      Filter: ({ column }) => {
        const interviewStatus = column.getFilterValue();
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
              marginTop: "0.2rem",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
              }}
            >
              <GreenButton
                label="Done"
                isActive={interviewStatus === 1}
                onClick={() => column.setFilterValue("1")}
              />
              <BlueButton
                label="Scheduled"
                isActive={interviewStatus === 0}
                onClick={() => column.setFilterValue("0")}
              />
            </div>
            <GrayButton label="Clear Filter" onClick={() => column.setFilterValue(undefined)} />
          </div>
        );
      },
    },
    {
      header: "Solved Questions",
      accessorKey: "highRatingCount",
      enableSorting: false,
      size: 100,
      Cell: ({ cell }) => {
        const count = cell?.row?.original?.highRatingCount;
        return count > 0 ? count : "-";
      },
    },
    isComponentAllowed("Interview Result") && {
      header: "Interview Result",
      enableSorting: false,
      accessorKey: "result",
      size: 100,
      Cell: ({ cell }) => {
        const result = cell?.row?.original?.result;

        const interviewTaken = cell?.row?.original?.interviewTaken;
        return (
          <span
            style={{
              color:
                result === "fail"
                  ? "#DD2D2D"
                  : result === "pass"
                  ? "green"
                  : result != null
                  ? "#FACC15"
                  : undefined,
              fontWeight: result != null ? 400 : undefined,
            }}
          >
            {interviewTaken === 0 || interviewTaken === null || result == null
              ? "-"
              : result
              ? result.toUpperCase()
              : "MAYBE"}
          </span>
        );
      },
      Filter: ({ column }) => {
        const result = column.getFilterValue();
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.25rem",
              marginTop: "0.2rem",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "0.25rem",
              }}
            >
              <GreenButton
                label="Pass"
                isActive={result === "pass"}
                onClick={() => column.setFilterValue("pass")}
              />
              <RedButton
                label="Fail"
                isActive={result === "fail"}
                onClick={() => column.setFilterValue("fail")}
              />
              <BlueButton
                label="Maybe"
                isActive={result === "maybe"}
                onClick={() => column.setFilterValue("maybe")}
              />
            </div>
            <GrayButton label="Clear Filter" onClick={() => column.setFilterValue(undefined)} />
          </div>
        );
      },
    },
    {
      header: "Start Date",
      accessorKey: "startDate",
      size: 180,
      Cell: ({ cell }) => {
        const date = cell?.row?.original?.startDate;

        function formatDate(dateString) {
          if (!dateString) return "-";
          const date = new Date(dateString);
          const istDate = new Date(date.getTime() + 5.5 * 60 * 60 * 1000); // Convert to IST
          return istDate.toLocaleDateString("en-GB"); // Display in DD/MM/YYYY
        }

        return <span>{formatDate(date)}</span>;
      },
      Filter: ({ column }) => {
        const startDate = column.getFilterValue() || "";

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                // Send date in 24-hour UTC format to backend
                column.setFilterValue(e.target.value); // Date will be in YYYY-MM-DD format
              }}
              style={{
                width: "170px",
                padding: "3px",
                border: "1px solid #ccc",
                borderRadius: "8px",
              }}
            />
            <GrayButton label="Clear Filter" onClick={() => column.setFilterValue(undefined)} />
          </div>
        );
      },
    },
    {
      header: "Start Time",
      accessorKey: "startTime",
      size: 180,
      Cell: ({ cell }) => {
        const time = cell?.row?.original?.startTime;

        function formatTime(timeString) {
          if (!timeString) return "-";
          const date = new Date(timeString);
          const istDate = new Date(date.getTime() - 5.5 * 60 * 60 * 1000);

          return istDate.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
        }
        return <span>{formatTime(time)}</span>;
      },
      Filter: ({ column }) => {
        const startTime = column.getFilterValue() || "";

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <input
              type="time"
              value={startTime}
              onChange={(e) => {
                const timeValue = e.target.value;

                const [hours, minutes] = timeValue.split(":").map(Number);
                const timeDate = new Date(1970, 0, 1, hours, minutes);

                const utcDate = new Date(timeDate.getTime() + 5.5 * 60 * 60 * 1000);
                const formattedTime = `${utcDate.toISOString().slice(11, 16)}:00`;

                column.setFilterValue(formattedTime);
              }}
              style={{
                width: "170px",
                padding: "3px",
                border: "1px solid #ccc",
                borderRadius: "8px",
              }}
            />
            <GrayButton label="Clear Filter" onClick={() => column.setFilterValue(undefined)} />
          </div>
        );
      },
    },
    {
      header: "Network Score",
      accessorKey: "networkScore",
      enableSorting: false,
      size: 100,
      Cell: ({ cell }) => {
        const score = parseFloat(cell?.row?.original?.networkScore);

        let color = "#808080";
        if (score >= 8) color = "green";
        else if (score >= 5) color = "#FACC15";
        else if (score > 0) color = "#DD2D2D";

        return (
          <span
            style={{
              color,
              fontWeight: 500,
            }}
          >
            {isNaN(score) ? "-" : score.toFixed(1)}
          </span>
        );
      },
      Filter: ({ column }) => {
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.25rem",
              marginTop: "0.2rem",
            }}
          >
            <input
              type="number"
              step="0.1"
              min="1"
              max="10"
              placeholder="Enter Network Score"
              value={column.getFilterValue() || ""}
              onChange={(e) => {
                const value = e.target.value;
                column.setFilterValue(value);
              }}
              style={{
                width: "170px",
                padding: "3px",
                border: "1px solid #ccc",
                borderRadius: "8px",
              }}
            />
            <GrayButton
              label="Clear Filter"
              onClick={() => {
                column.setFilterValue("");
              }}
            />
          </div>
        );
      },
    },
    isComponentAllowed("Cheating Score") && {
      header: "Cheating Score",
      accessorKey: "cheatingScore",
      size: 180,
      Cell: ({ cell }) => {
        const score = cell?.row?.original?.cheatingScore;

        return <span>{score ? `${score}%` : `-`}</span>;
      },
    },
    isComponentAllowed("Display Count") && {
      header: "Display Count",
      accessorKey: "displayCount",
      size: 180,
      Cell: ({ cell }) => {
        const val = cell?.row?.original?.displayCount;
        return <span>{val == null ? "-" : val}</span>;
      },
    },
    isComponentAllowed("Screen Extended") && {
      header: "Screen Extended",
      accessorKey: "screenExtended",
      size: 180,
      Cell: ({ cell }) => {
        const val = cell.row.original.screenExtended;
        return <span>{val === 1 ? "Yes" : val === 0 ? "No" : "-"}</span>;
      },
      Filter: ({ column }) => {
        // current value of this column's filter
        const filterValue = column.getFilterValue();
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.25rem",
              marginTop: "0.2rem",
            }}
          >
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <GreenButton
                label="Yes"
                isActive={filterValue === "1"}
                onClick={() => column.setFilterValue("1")}
              />
              <RedButton
                label="No"
                isActive={filterValue === "0"}
                onClick={() => {
                  column.setFilterValue("0");
                }}
              />
            </div>
            <GrayButton label="Clear Filter" onClick={() => column.setFilterValue(undefined)} />
          </div>
        );
      },
      filterFn: "equals",
    },
    {
      header: "Notes",
      accessorKey: "notes",
      size: 180,
      Cell: ({ cell }) => {
        const val = cell?.row?.original?.notes;
        const id = cell?.row?.original?.viewMappingId;
        return (
          <span style={{ display: "flex", gap: "0.5rem" }} onClick={(e) => e.stopPropagation()}>
            {val == null ? (
              <UpdateCandidateNotes label="Add Notes" viewId={viewId} id={id} />
            ) : (
              <UpdateCandidateNotes label="View Notes" viewId={viewId} id={id} notes={val} />
            )}
          </span>
        );
      },
    },
  ].filter(Boolean);

  const table = useMaterialReactTable({
    columns,
    data: data?.candidates || [],
    initialState: { showColumnFilters: false },
    onColumnFiltersChange: setColumnFilters,
    manualFiltering: true,
    onGlobalFilterChange: setGlobalFilter,
    manualPagination: true,
    onPaginationChange: setPagination,
    manualSorting: true,
    onSortingChange: setSorting,
    enablePagination: true,
    enableGlobalFilter: false,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection, // Update rowSelection state

    rowCount: data.total || 0,
    // paginationDisplayMode: "pages",
    enableColumnResizing: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    state: {
      rowSelection,
      pagination,
      showSkeletons: isLoading,
      showProgressBars: isRefetching,
      showAlertBanner: isError,
      sorting,
      columnFilters,
      globalFilter,
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
        background: "white",
      },
    },
    muiTableContainerProps: {
      sx: {
        display: "flex",
        flex: 1,
        background: "white",
        tableLayout: "fixed",
        "& .MuiTable-root": {
          borderCollapse: "separate",
          borderSpacing: 0,
        },
        "&::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f5f9",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#cbd5e1",
          borderRadius: "4px",
          "&:hover": {
            background: "#94a3b8",
          },
        },
      },
    },
    muiTableBodyProps: {
      sx: {
        "& tr": {
          background: "white",
          transition: "all 0.2s ease-in-out",
          position: "relative",
          "&:hover": {
            "& td": {
              color: "#0369a1",
            },
          },
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        padding: "16px 10px",
        fontSize: "14px",
        color: "#334155",
        background: "white",
        fontWeight: 400,
        lineHeight: "20px",
        "&:first-of-type": {
          paddingLeft: "24px",
        },
        "&:last-of-type": {
          paddingRight: "24px",
        },
      },
    },
    muiTableHeadProps: {
      sx: {
        background: "white",
        position: "sticky",
        top: 0,
        padding: 0,
        zIndex: 2,
      },
    },
    muiTableHeadCellProps: {
      sx: {
        borderBottom: "1px solid #e2e8f0",
        background: "#f8fafc",
        color: "#333333",
        fontSize: "12px",
        fontWeight: 600,
        lineHeight: "16px",
        letterSpacing: "0.05em",
        padding: "14px 10px",
        "&:first-of-type": {
          borderTopLeftRadius: "12px",
          paddingLeft: "24px",
        },
        "&:last-of-type": {
          borderTopRightRadius: "12px",
          paddingRight: "24px",
        },
      },
    },
    muiPaginationProps: {
      rowsPerPageOptions: [5, 10, 20, 30],
      sx: {
        ".MuiToolbar-root": {
          borderTop: "1px solid #e2e8f0",
          padding: "16px 24px",
          background: "white",
          color: "#64748b",
          borderBottomLeftRadius: "12px",
          borderBottomRightRadius: "12px",
          ".MuiSelect-select": {
            fontSize: "14px",
          },
          ".MuiTablePagination-displayedRows": {
            fontSize: "14px",
          },
          ".MuiButtonBase-root": {
            color: "#64748b",
            "&:hover": {
              backgroundColor: "#f1f5f9",
            },
          },
        },
      },
    },
    getRowId: (row) => row.viewMappingId,

    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        const isCheckbox = event.target.closest('input[type="checkbox"]');
        if (!isCheckbox) {
          navigate(`/admin/${version}/dashboard/form/${formId}/candidate/${row.original.id}`);
        }
      },
      sx: { cursor: "pointer" },
    }),
  });

  if (isError)
    return (
      <div
        style={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <BlockIcon style={{ color: "red", height: "4rem", width: "4rem" }} />
          <p style={{ fontWeight: "700", color: "gray", fontSize: "1.3rem" }}>
            You cannot access this page
          </p>
        </div>
      </div>
    );

  return (
    <div className="flex flex-1 w-full h-full flex-col">
      <div className="bg-white border-b sticky top-0 z-50 shadow-sm">
        <div className="px-6">
          <div className="flex items-center justify-between h-11">
            <div className="flex items-center space-x-4">
              <div className="bg-blue-50 border border-blue-200 hover:bg-blue-100 cursor-default px-4 py-1.5 flex items-center rounded-lg">
                <span className="text-blue-700 text-sm font-medium">View: {viewData.name}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="px-4 py-4 mx-auto w-full max-w-[calc(100vw-223px-32px)]">
        <MaterialReactTable table={table} />
      </div>
    </div>
  );
};

FormViewsCandidatesTable.propTypes = {
  version: PropTypes.string.isRequired,
  viewId: PropTypes.number.isRequired,
  token: PropTypes.string.isRequired,
  viewData: PropTypes.object.isRequired,
};

export default FormViewsCandidatesTable;
